// Per-map world->pixel calibration. Values mirror CS radar .txt semantics:
//   worldTopLeftX = pos_x (world X at the LEFT edge of the image)
//   worldTopLeftY = pos_y (world Y at the TOP edge of the image)
//   scale         = world units per source-image pixel
//   imageSize     = source overview image edge length in pixels (square)
export const MAP_CALIBRATION = {
  // PROVISIONAL: worldTopLeftX/Y and scale are placeholder values for this
  // overview image and need to be tuned against real radar data before
  // player dots will line up precisely on the rendered map.
  de_mirage: {
    imageUrl: "https://static.wikia.nocookie.net/cswikia/images/f/f5/De_mirage_cs2.png",
    worldTopLeftX: -3230,
    worldTopLeftY: 1713,
    scale: 5.0,
    imageSize: 1024,
  },
};

export function getMapCalibration(mapId) {
  return MAP_CALIBRATION[mapId] || null;
}

// Convert a world {x, y} to pixel coordinates for a rendered image of `renderedSize` px.
export function worldToPixel(worldPos, calibration, renderedSize) {
  const sourcePx = (worldPos.x - calibration.worldTopLeftX) / calibration.scale;
  const sourcePy = (calibration.worldTopLeftY - worldPos.y) / calibration.scale;
  const ratio = (renderedSize || calibration.imageSize) / calibration.imageSize;
  return { px: sourcePx * ratio, py: sourcePy * ratio };
}

// Parse a CS2 `getpos` / `getpos_exact` console line into world coordinates.
// Accepts the full "setpos <x> <y> <z>;setang ..." string the game prints, and
// also falls back to a bare "<x> <y>" paste. Returns { x, y, z } or null.
export function parseGetPos(text) {
  if (!text || typeof text !== "string") return null;
  const m = text.match(
    /setpos(?:_exact)?\s+(-?[\d.eE+]+)\s+(-?[\d.eE+]+)(?:\s+(-?[\d.eE+]+))?/i
  );
  if (m) {
    return { x: parseFloat(m[1]), y: parseFloat(m[2]), z: m[3] != null ? parseFloat(m[3]) : null };
  }
  const nums = text.match(/-?[\d.]+/g);
  if (nums && nums.length >= 2) {
    return { x: parseFloat(nums[0]), y: parseFloat(nums[1]), z: nums[2] != null ? parseFloat(nums[2]) : null };
  }
  return null;
}

// Solve a 3x3 linear system M·x = r via Gaussian elimination with partial
// pivoting. Returns [x0, x1, x2] or null if (near-)singular.
function solve3(M, r) {
  const a = [
    [M[0][0], M[0][1], M[0][2], r[0]],
    [M[1][0], M[1][1], M[1][2], r[1]],
    [M[2][0], M[2][1], M[2][2], r[2]],
  ];
  for (let col = 0; col < 3; col += 1) {
    let piv = col;
    for (let row = col + 1; row < 3; row += 1) {
      if (Math.abs(a[row][col]) > Math.abs(a[piv][col])) piv = row;
    }
    if (Math.abs(a[piv][col]) < 1e-9) return null;
    const tmp = a[col];
    a[col] = a[piv];
    a[piv] = tmp;
    const pivVal = a[col][col];
    for (let c = col; c < 4; c += 1) a[col][c] /= pivVal;
    for (let row = 0; row < 3; row += 1) {
      if (row === col) continue;
      const f = a[row][col];
      for (let c = col; c < 4; c += 1) a[row][c] -= f * a[col][c];
    }
  }
  return [a[0][3], a[1][3], a[2][3]];
}

// Least-squares fit of the world->pixel calibration from collected sample
// points. Each point is { srcPx, srcPy, worldX, worldY } where srcPx/srcPy are
// in SOURCE-image pixels. Solves, with a single shared `scale`:
//   worldX = worldTopLeftX + scale * srcPx
//   worldY = worldTopLeftY - scale * srcPy
// Returns { worldTopLeftX, worldTopLeftY, scale, residuals[], maxError } or
// { error } when the input is insufficient / degenerate.
export function computeCalibration(points) {
  const pts = (points || []).filter(
    (p) =>
      p &&
      Number.isFinite(p.srcPx) &&
      Number.isFinite(p.srcPy) &&
      Number.isFinite(p.worldX) &&
      Number.isFinite(p.worldY)
  );
  if (pts.length < 2) {
    return { error: "Need at least 2 points that have both a map click and world coordinates." };
  }

  const M = [
    [0, 0, 0],
    [0, 0, 0],
    [0, 0, 0],
  ];
  const r = [0, 0, 0];
  for (const p of pts) {
    // X equation, coefficients [1, 0, srcPx]
    M[0][0] += 1;
    M[0][2] += p.srcPx;
    M[2][0] += p.srcPx;
    M[2][2] += p.srcPx * p.srcPx;
    r[0] += p.worldX;
    r[2] += p.srcPx * p.worldX;
    // Y equation, coefficients [0, 1, -srcPy]
    M[1][1] += 1;
    M[1][2] += -p.srcPy;
    M[2][1] += -p.srcPy;
    M[2][2] += p.srcPy * p.srcPy;
    r[1] += p.worldY;
    r[2] += -p.srcPy * p.worldY;
  }

  const sol = solve3(M, r);
  if (!sol) {
    return { error: "Points are collinear or too close together — spread them across the map." };
  }
  const [worldTopLeftX, worldTopLeftY, scale] = sol;
  if (!(scale > 0)) {
    return { error: "Computed a non-positive scale — check that world coords match the clicked spots." };
  }

  const residuals = pts.map((p) => {
    const predX = worldTopLeftX + scale * p.srcPx;
    const predY = worldTopLeftY - scale * p.srcPy;
    return Math.hypot(predX - p.worldX, predY - p.worldY);
  });
  const maxError = residuals.reduce((m, e) => Math.max(m, e), 0);

  return {
    worldTopLeftX: Math.round(worldTopLeftX * 100) / 100,
    worldTopLeftY: Math.round(worldTopLeftY * 100) / 100,
    scale: Math.round(scale * 1000) / 1000,
    residuals,
    maxError: Math.round(maxError * 100) / 100,
  };
}
